import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useCallback } from "react";
import { sharedMutators } from "../../shared/mutators";
import { useToast } from "../components/Toast";
import { executePushLive, type PushLiveBranch } from "../lib/executePushLive";
import { playPushLive } from "../lib/sounds";
import { navigate, routes } from "../lib/urls";

export function usePushLive({
  branchId,
  spaceId,
  onAccept,
  onDismiss,
  onDeploying,
  onMerging,
}: {
  branchId: string;
  spaceId: string;
  onAccept?: () => void;
  onDismiss?: () => void;
  onDeploying?: (v: boolean) => void;
  onMerging?: (v: boolean) => void;
}) {
  const z = useZero();
  const { showToast } = useToast();

  const handleMergeMain = useCallback(() => {
    onMerging?.(true);
    const { server } = z.mutate(
      sharedMutators.mergeMainIntoBranch({ branchId })
    );
    void server.then(() => onMerging?.(false));
  }, [z, branchId, onMerging]);

  const pushLive = useCallback(
    (branch: PushLiveBranch | undefined, mainBranchId: string | undefined) => {
      void executePushLive({
        branch,
        mainBranchId,
        onAccept,
        onDismiss,
        submitPush: (sourceBranchId, message) =>
          z.mutate(
            sharedMutators.pushBranchToLive({
              sourceBranchId,
              newCommitId: nanoid(12),
              message,
            })
          ),
        onPushed: (b, mainId) => {
          onDeploying?.(true);
          playPushLive();
          navigate(routes.space.workflow, {
            spaceId,
            workflowId: b.workflowId,
            branchId: mainId ?? "",
          });
        },
        onPushFailed: (b, error) => {
          const conflict = error.details?.code === "behind-with-conflicts";
          showToast({
            icon: "circle-off",
            message: error.message,
            buttonLabel: conflict ? "Pull main in" : "Dismiss",
            onButtonClick: conflict ? handleMergeMain : undefined,
          });
          navigate(routes.space.workflow, {
            spaceId,
            workflowId: b.workflowId,
            branchId: b.id,
          });
        },
      })?.finally(() => onDeploying?.(false));
    },
    [z, showToast, handleMergeMain, spaceId, onAccept, onDismiss, onDeploying]
  );

  return { pushLive };
}
